/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, cx, jsx } from '@atlaskit/css';
import InlineEdit, { type InlineEditProps } from '@atlaskit/inline-edit';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import { CustomTooltip } from './CustomTooltip';
import { ValidationPopup } from './ValidationPopup';

const messages = defineMessages({
	none: {
		id: 'jira.jira-platform-fields.jira-inline-field.none',
		defaultMessage: 'None',
		description: 'Display text shown when no value has been set for the field',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		// The InlineEdit component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	readView: {
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editableReadView: {
		width: '100%',
		cursor: 'pointer',
		height: '36px', // 40px - 2 * 2px border
	},
});

export interface ReadViewProps<FieldValue> {
	value: FieldValue;
}

export interface JiraInlineFieldProps<FieldValue> extends Omit<
	InlineEditProps<FieldValue>,
	'readView' | 'validate'
> {
	/** Optional custom container styles */
	containerStyles?: ReturnType<typeof cssMap>[string];
	/** Custom callback to optionally display an inline dialog with a message
	 * when the field input is invalid. Note that this logic will only run after
	 * the built-in validation logic, which validates required fields.
	 */
	customValidate?: InlineEditProps<FieldValue>['validate'];
	/** The name of the field (used for validation messages and analytics) */
	fieldName?: string;
	/** The field type identifier for analytics */
	fieldTypeForAnalytics: string;
	/** Optional flag to control whether to display the 'None' placeholder if
	 * the field is empty.
	 */
	hideEmptyPlaceholder?: boolean;
	/** Whether the field is editable */
	isEditable: boolean;
	/** Whether the field is required */
	isRequired: boolean;
	/** Render prop for read view */
	readView: (props: ReadViewProps<FieldValue>) => React.ReactNode;
	/** Optional text to render as a tooltip for the field. Note that the
	 * tooltip will only show if the text were to be truncated in the field's
	 * container.
	 */
	tooltipText?: string;
}

/**
 * `JiraInlineField` is a wrapper component that centralizes common logic for
 * Jira inline field components.
 */
export const JiraInlineField = <FieldValue extends unknown = string>({
	containerStyles,
	defaultValue,
	editView,
	fieldName,
	fieldTypeForAnalytics,
	hideActionButtons = true,
	hideEmptyPlaceholder = false,
	isEditable,
	isRequired,
	onCancel,
	onConfirm,
	onEdit,
	readView,
	readViewFitContainerWidth = true,
	tooltipText,
	customValidate,
	...props
}: JiraInlineFieldProps<FieldValue>):
	| string
	| number
	| boolean
	| JSX.Element
	| Iterable<React.ReactNode>
	| null
	| undefined => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isEmpty = useMemo(
		() =>
			defaultValue == null ||
			(typeof defaultValue === 'string' && defaultValue.trim() === '') ||
			(Array.isArray(defaultValue) && defaultValue.length === 0),
		[defaultValue],
	);

	const requiredValidation = useValidateRequiredField({
		isRequired,
		isEditable,
		fieldName,
		fieldTypeForAnalytics,
	});

	const combinedValidate = useCallback(
		(value: FieldValue, formState: object, fieldState: object) => {
			// Required validation first
			const requiredError = requiredValidation(value);
			if (requiredError) {
				return requiredError;
			}
			// Then custom validation if provided
			return customValidate?.(value, formState, fieldState);
		},
		[requiredValidation, customValidate],
	);

	// Analytics-wrapped handlers
	const handleEdit = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'initiated',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: fieldTypeForAnalytics,
				isRequired,
				isEditable,
			},
		);
		onEdit?.();
	}, [createAnalyticsEvent, fieldName, fieldTypeForAnalytics, isRequired, isEditable, onEdit]);

	const handleConfirm = useCallback(
		(value: FieldValue, analyticsEvent: Parameters<typeof onConfirm>[1]) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'applied',
					actionSubject: 'inlineEdit',
				}),
				{
					fieldName,
					fieldType: fieldTypeForAnalytics,
					isRequired,
					isEditable,
				},
			);
			onConfirm(value, analyticsEvent);
		},
		[createAnalyticsEvent, fieldName, fieldTypeForAnalytics, isRequired, isEditable, onConfirm],
	);

	const handleCancel = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'cancelled',
				actionSubject: 'inlineEdit',
			}),
			{
				fieldName,
				fieldType: fieldTypeForAnalytics,
				isRequired,
				isEditable,
			},
		);
		onCancel?.();
	}, [createAnalyticsEvent, fieldName, fieldTypeForAnalytics, isRequired, isEditable, onCancel]);

	// Rely on a couple of refs to determine whether a tooltip is needed:
	// 1. Ref to the container Box that wraps the read view content. Used to
	// measure available width for text.
	const readViewContainerRef = useRef<HTMLDivElement | null>(null);
	// 2. Ref to a cached canvas element to avoid creating a new one on every
	// hover. Used to simulate how much width would the text take up on the
	// screen.
	const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);

	const canAppear = useCallback(() => {
		if (!tooltipText || !readViewContainerRef.current) {
			return false;
		}

		const container = readViewContainerRef.current;
		const computedStyle = window.getComputedStyle(container);

		// Build the font string from the container's computed styles so the
		// measurement matches the actual rendering context.
		const font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

		if (!measureCanvasRef.current) {
			measureCanvasRef.current = getDocument()?.createElement('canvas') ?? null;
		}
		const ctx = measureCanvasRef.current?.getContext('2d');
		if (ctx == null) {
			return false;
		}
		ctx.font = font;
		const textWidth = ctx.measureText(tooltipText).width;

		// Subtract horizontal padding to get the actual available content width
		const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
		const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
		const availableWidth = container.clientWidth - paddingLeft - paddingRight;

		return textWidth > availableWidth;
	}, [tooltipText]);

	const enrichedReadView: InlineEditProps<FieldValue>['readView'] = useCallback(() => {
		const containedReadView = (
			<Box
				ref={readViewContainerRef}
				xcss={cx(styles.readView, isEditable && styles.editableReadView)}
			>
				{isEmpty ? (
					<Text align="start" color="color.text.subtle" maxLines={1}>
						{hideEmptyPlaceholder ? null : formatMessage(messages.none)}
					</Text>
				) : (
					readView({
						value: defaultValue,
					})
				)}
			</Box>
		);

		return tooltipText ? (
			<Tooltip component={CustomTooltip} content={tooltipText} canAppear={canAppear}>
				{containedReadView}
			</Tooltip>
		) : (
			containedReadView
		);
	}, [
		canAppear,
		defaultValue,
		formatMessage,
		hideEmptyPlaceholder,
		isEditable,
		isEmpty,
		readView,
		tooltipText,
	]);

	const enrichedEditView: InlineEditProps<FieldValue>['editView'] = useCallback(
		(fieldProps, ref) => {
			const { errorMessage, isInvalid, ...restFieldProps } = fieldProps;

			return (
				<ValidationPopup
					errorMessage={errorMessage}
					showInvalidPopup={isInvalid && !!errorMessage}
					trigger={({ ref: popupRef, ...triggerProps }) => (
						<Box ref={popupRef}>
							{editView({ ...restFieldProps, errorMessage, isInvalid, ...triggerProps }, ref)}
						</Box>
					)}
				/>
			);
		},
		[editView],
	);

	// Read-only view
	if (!isEditable) {
		return enrichedReadView();
	}

	// Editable view
	return (
		<Box xcss={containerStyles ?? styles.container}>
			<InlineEdit<FieldValue>
				{...props}
				defaultValue={defaultValue}
				hideActionButtons={hideActionButtons}
				readViewFitContainerWidth={readViewFitContainerWidth}
				validate={combinedValidate}
				onConfirm={handleConfirm}
				onEdit={handleEdit}
				onCancel={handleCancel}
				readView={enrichedReadView}
				editView={enrichedEditView}
			/>
		</Box>
	);
};
